import React from "react";
import { useTableV2State } from "../../../../components/TableV2/hooks/useTableV2State";
import { Box, Tooltip, Typography, Chip } from "@mui/material";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../components/TableV2/interfaces/IDataTableV2Props";
import { DataTableV2 } from "../../../../components/TableV2/DataTableV2";
import { TABLE_CACHING_KEYS } from "../../../../constants/constants";
import { StatusRenderer } from "../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { Visibility as VisibilityIcon, TrendingUp } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatchWrapper } from "../../../../hooks";
import { upsertUpsellLeadsAsync } from "../../../../redux/upsellLead/upsellLeadsActions";
import { IUpsertUpsellLeadPayload } from "../../../../redux/upsellLead/upsellLeads.types";
import { useBillingCompanyContext } from "../../../../providers";
import { ReadMoreText } from "../../../marketingCampaign/components/ReadMoreText";
import {
  IFoodLead,
  IServiceCategoryLead,
  IVenueLead,
} from "../../types/upsell.types";
import { getBookingPeriodLabel } from "../../constants";
import UpsellServiceCards from "./UpsellServiceCards";
import { Dialog } from "../../../../components/Dialogs/Dialog";
import { LeadStatusRenderer } from "../../../../components/LeadStatusRenderer/LeadStatusRenderer";
import { useTimezoneFormatter } from "../../../../hooks/useTimezoneFormatter";

interface IUpsellServiceTableV2Props {
  leadsData: any[];
  referenceType?: "SERVICE" | "VENUE" | "FOOD_ITEM";
  referenceUuid?: string;
  selectedBookingPeriod?: number;
}

export const UpsellServiceTableV2: React.FC<IUpsellServiceTableV2Props> = ({
  leadsData,
  referenceType = "SERVICE",
  referenceUuid = "",
  selectedBookingPeriod,
}) => {
  const tableRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatchWrapper();
  const company = useBillingCompanyContext();
  const { ftDate } = useTimezoneFormatter();
  const [openLeadsData, setOpenLeadsData] = React.useState<any[]>([]);

  const handleViewLead = (lead: any) => {
    setOpenLeadsData([lead]);
  };

  // Helper to get reference UUID from lead's upselling_services array or upselling_venue object
  const getReferenceUuidValue = (
    lead: IServiceCategoryLead | IVenueLead | any,
  ) => {
    if (referenceType === "SERVICE") {
      // upselling_services is an array of service items
      const upsellingServices = (lead as IServiceCategoryLead)
        .upselling_services;
      if (Array.isArray(upsellingServices) && upsellingServices.length > 0) {
        return upsellingServices[0]?.service_item_uuid || "";
      }
      return "";
    } else if (referenceType === "VENUE") {
      // upselling_venue is a single venue object
      return (lead as IVenueLead).upselling_venue?.venue_uuid || "";
    } else if (referenceType === "FOOD_ITEM") {
      // upselling_food_item is a single food object

      const upsellingFood = (lead as IFoodLead).upselling_food_item;
      if (Array.isArray(upsellingFood) && upsellingFood.length > 0) {
        return upsellingFood[0]?.food_item_uuid || "";
      }
      return "";
    }
  };

  // Helper to get upsell service/venue name from lead
  const getUpsellServiceVenueName = (
    lead: IServiceCategoryLead | IVenueLead | IFoodLead | any,
  ) => {
    if (referenceType === "SERVICE") {
      // upselling_services is an array of service items
      const upsellingServices = (lead as IServiceCategoryLead)
        .upselling_services;
      if (Array.isArray(upsellingServices) && upsellingServices.length > 0) {
        return upsellingServices[0]?.service_item_name || "";
      }
      return "";
    } else if (referenceType === "VENUE") {
      // upselling_venue is a single venue object
      return (lead as IVenueLead).upselling_venue?.venue_name || "";
    } else if (referenceType === "FOOD_ITEM") {
      // upselling_food_item is a single food object

      const upsellingFood = (lead as IFoodLead).upselling_food_item;
      if (Array.isArray(upsellingFood) && upsellingFood.length > 0) {
        return upsellingFood[0]?.item_name || "";
      }
      return "";
    }
  };

  const handleCreateUpsellLead = (lead: any) => {
    // Build the upsell opportunity duration category using week/month labels
    const upsellOpportunityDurationCategory = selectedBookingPeriod
      ? getBookingPeriodLabel(selectedBookingPeriod)
      : "";

    const payload: IUpsertUpsellLeadPayload = {
      upsell_leads_uuid: null,
      leads_uuid: lead.leads_uuid,
      reference_uuid_column_name: referenceType,
      reference_uuid_column_value: getReferenceUuidValue(lead) || "",
      reason_for_upsell: lead.reason_for_upsell,
      followup_date: null,
      upsell_opportunity_duration_category: upsellOpportunityDurationCategory,
      description: "",
      quantity: 0,
      company_outlet_uuid: company?.comapny?.company_outlet_uuid || "",
      status: "ONGOING",
    };

    dispatch(
      upsertUpsellLeadsAsync([payload], (isSuccess, responseData) => {
        if (isSuccess) {
          // Pass the newly created upsell lead UUID through navigation state
          // to auto-open the edit panel on the upsell leads page
          const newUpsellLeadUuid = responseData?.data?.[0]?.upsell_leads_uuid;
          navigate(`/upsell-leads`, {
            state: { openEditPanelForUuid: newUpsellLeadUuid },
          });
        }
      }),
    );
  };

  const {
    state: { masterColumnResize, pagination },

    onResizeMasterColumns,
    onPageChange,
    onRowsPerPageChange,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "last30Days",
      selectedTab: "-1",
    },
    pagination: {
      pageNumber: 1,
      rowsInPerPage: 10,
      apiFetchRowCount: leadsData.length,
      extraFetchFactor: 1,
    },
    cachingFilters: {
      uniqueKey: TABLE_CACHING_KEYS.LEAD_TABLE_COUNT,
    },
  });

  const upsellServiceTableProps: IDataTableV2Props = {
    isPagination: true,
    pageNumber: pagination.pageNumber,
    rowsPerPageOptions: pagination.rowsInPerPage,
    extraFetchFactor: pagination.extraFetchFactor,
    isDataLoading: false,

    tableCommandBarProps: {
      preDefinedPlugins: {},
      leftItems: {
        customPlugins: [],
      },
      rightItems: {
        customPlugins: [],
      },
    },

    mobileColumns: [
      {
        variant: "template1",
        headingColumn: {
          key: "name_of_guest",
          fieldName: "name_of_guest",
        },
        rightHeadingColumn: {
          key: "lead_code",
          fieldName: "lead_code",
        },
        subSectionColumn: [
          {
            key: "event_type",
            fieldName: "event_type",
          },
        ],
        otherColumns: [
          {
            key: "contact_number_one",
            fieldName: "contact_number_one",
            headerName: "Contact",
          },
          {
            key: "function_date",
            headerName: "Event Date",
            fieldName: "function_date",
            onRowCellRender: (value, row: any) => {
              return ftDate(row.function_date);
            },
          },
          {
            key: "number_of_guests",
            fieldName: "number_of_guests",
            headerName: "Guests",
          },
        ],

        actions: [
          {
            name: "view",
            icon: <VisibilityIcon />,
            click: (row: any) => {
              handleViewLead(row);
            },
          },

          {
            name: "Create Upsell Lead",
            icon: <TrendingUp />,
            click: (row: any) => {
              handleCreateUpsellLead(row);
            },
          },
        ],
      },
    ],

    masterColumns: [
      {
        key: "actions",
        width: "70px",
        headerName: "Actions",
        fieldName: "",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        isFirstColumnSticky: true,

        onRowCellRender: (value, row: any) => {
          return (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title="View Lead">
                <VisibilityIcon
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    fontSize: 20,
                  }}
                  onClick={() => handleViewLead(row)}
                />
              </Tooltip>

              <Tooltip title="Create Upsell Lead">
                <TrendingUp
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    fontSize: 20,
                  }}
                  onClick={() => handleCreateUpsellLead(row)}
                />
              </Tooltip>
            </Box>
          );
        },
      },

      {
        key:
          referenceType === "SERVICE"
            ? "upsell_service"
            : referenceType === "VENUE"
              ? "upsell_venue"
              : referenceType === "FOOD_ITEM"
                ? "upsell_food"
                : "--",
        width: "300px",
        headerName:
          referenceType === "SERVICE"
            ? "Upsell Service"
            : referenceType === "VENUE"
              ? "Upsell Venue"
              : referenceType === "FOOD_ITEM"
                ? "Upsell Food"
                : "--",
        fieldName:
          referenceType === "SERVICE"
            ? "upsell_service"
            : referenceType === "VENUE"
              ? "upsell_venue"
              : referenceType === "FOOD_ITEM"
                ? "upsell_food"
                : "--",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,

        onRowCellRender: (value, row: any) => {
          const displayName = getUpsellServiceVenueName(row);
          return displayName ? <StatusRenderer status={displayName} /> : "--";
        },
      },

      {
        key: "status",
        width: "300px",
        headerName: "Lead Status",
        fieldName: "status",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,

        onRowCellRender: (value, row: any) => {
          return (
            <LeadStatusRenderer
              status={row.status || ""}
              actualStatus={row.derived_status || ""}
            />
          );
        },
      },

      {
        key: "name_of_guest",
        headerName: "Guest Name",
        fieldName: "name_of_guest",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
        width: "300px",
        onRowCellRender: (value, row: any) => {
          return (
            <Typography variant="body2" fontWeight={600}>
              {row.name_of_guest}
            </Typography>
          );
        },
      },

      {
        key: "reason_for_upsell",
        headerName: "Reason for Upsell",
        fieldName: "reason_for_upsell",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
        width: "250px",
        onRowCellRender: (value, row: any) => {
          return (
            <ReadMoreText
              text={row.reason_for_upsell}
              maxLines={2}
              variant="body2"
            />
          );
        },
      },

      {
        key: "contact_number_one",
        headerName: "Contact",
        fieldName: "contact_number_one",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
        width: "100px",
        onRowCellRender: (value, row: any) => {
          return (
            <Typography variant="body2">
              {row.contact_number_one || "--"}
            </Typography>
          );
        },
      },

      {
        key: "event_type",
        headerName: "Event Type",
        fieldName: "event_type",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
        width: "120px",
        onRowCellRender: (value, row: any) => {
          return (
            <Chip
              label={row.event_type}
              size="small"
              color="primary"
              variant="outlined"
            />
          );
        },
      },

      {
        key: "function_date",
        headerName: "Event Date",
        fieldName: "function_date",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        width: "100px",
        onRowCellRender: (value, row: any) => {
          return ftDate(row.function_date);
        },
      },

      {
        key: "number_of_guests",
        headerName: "Guests",
        fieldName: "number_of_guests",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
        width: "100px",
      },

      {
        key: "preferred_venue",
        headerName: "Venue",
        fieldName: "preferred_venue",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
        width: "150px",
        onRowCellRender: (value, row: any) => {
          return (
            <Typography variant="body2">
              {row.preferred_venue || "--"}
            </Typography>
          );
        },
      },

      {
        key: "event_time",
        headerName: "Event Time",
        fieldName: "event_time",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
        width: "100px",
        onRowCellRender: (value, row: any) => {
          return (
            <Typography variant="body2">{row.event_time || "--"}</Typography>
          );
        },
      },

      {
        key: "handled_by_employee",
        width: "100px",
        headerName: "Handled By",
        fieldName: "handled_by_employee",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
      },
    ],

    rows: leadsData,

    onPageChange: onPageChange,
    onRowsPerPageChange: onRowsPerPageChange,

    masterColumnResize: {
      values: masterColumnResize,
      onResize: onResizeMasterColumns,
    },
  };

  return (
    <>
      <Box
        ref={tableRef}
        sx={{
          width: "100%",
          minWidth: "1000px", // Ensure minimum width to trigger horizontal scroll
          overflowX: "auto",
        }}
      >
        <DataTableV2 {...upsellServiceTableProps} />
      </Box>

      <Dialog
        open={openLeadsData.length > 0}
        onClose={() => setOpenLeadsData([])}
        title="Upsell Leads"
        size="xs"
      >
        <UpsellServiceCards
          leadsData={openLeadsData}
          referenceType={referenceType}
          referenceUuid={referenceUuid}
          selectedBookingPeriod={selectedBookingPeriod}
        />
      </Dialog>
    </>
  );
};
